export type PointerRole = "bubble" | "current";

export interface PointerSample {
  id: number;
  x: number;
  y: number;
  dx: number;
  dy: number;
  role: PointerRole;
  /** 1 = referência; escala com a aceleração do rato. */
  intensity: number;
}

type Handler<T> = (payload: T) => void;

type TrackRole = "pending" | PointerRole;

interface TrackedPointer {
  x: number;
  y: number;
  originX: number;
  originY: number;
  role: TrackRole;
  dwellTimer: number | null;
}

interface Kinematics {
  x: number;
  y: number;
  vx: number;
  vy: number;
  ax: number;
  ay: number;
  t: number;
}

export class PointerInput {
  private readonly pointers = new Map<number, TrackedPointer>();
  private readonly motion = new Map<number, Kinematics>();
  private readonly bubbleStartHandlers: Handler<PointerSample>[] = [];
  private readonly bubbleMoveHandlers: Handler<PointerSample>[] = [];
  private readonly bubbleEndHandlers: Handler<PointerSample>[] = [];
  private readonly currentStrokeHandlers: Handler<PointerSample>[] = [];
  private readonly currentEndHandlers: Handler<PointerSample>[] = [];
  private readonly hoverHandlers: Handler<PointerSample>[] = [];
  private readonly hoverEndHandlers: Handler<void>[] = [];

  constructor(
    private readonly target: HTMLElement,
    private readonly accelRef: () => number,
    private readonly accelMax: () => number,
    private readonly touchDwellMs: () => number,
    private readonly touchSlopPx: () => number,
  ) {
    this.onDown = this.onDown.bind(this);
    this.onMove = this.onMove.bind(this);
    this.onUp = this.onUp.bind(this);
    this.onLeave = this.onLeave.bind(this);

    target.addEventListener("pointerdown", this.onDown);
    target.addEventListener("pointermove", this.onMove);
    target.addEventListener("pointerup", this.onUp);
    target.addEventListener("pointercancel", this.onUp);
    target.addEventListener("pointerleave", this.onLeave);
    target.addEventListener("contextmenu", (e) => e.preventDefault());
    target.addEventListener(
      "lostpointercapture",
      (e) => this.onUp(e as PointerEvent),
    );
  }

  destroy(): void {
    this.target.removeEventListener("pointerdown", this.onDown);
    this.target.removeEventListener("pointermove", this.onMove);
    this.target.removeEventListener("pointerup", this.onUp);
    this.target.removeEventListener("pointercancel", this.onUp);
    this.target.removeEventListener("pointerleave", this.onLeave);
  }

  onBubbleStart(fn: Handler<PointerSample>): void {
    this.bubbleStartHandlers.push(fn);
  }

  onBubbleMove(fn: Handler<PointerSample>): void {
    this.bubbleMoveHandlers.push(fn);
  }

  onBubbleEnd(fn: Handler<PointerSample>): void {
    this.bubbleEndHandlers.push(fn);
  }

  onCurrentStroke(fn: Handler<PointerSample>): void {
    this.currentStrokeHandlers.push(fn);
  }

  onCurrentEnd(fn: Handler<PointerSample>): void {
    this.currentEndHandlers.push(fn);
  }

  onHover(fn: Handler<PointerSample>): void {
    this.hoverHandlers.push(fn);
  }

  onHoverEnd(fn: Handler<void>): void {
    this.hoverEndHandlers.push(fn);
  }

  dispose(): void {
    for (const p of this.pointers.values()) this.clearDwell(p);
    this.pointers.clear();
    this.target.removeEventListener("pointerdown", this.onDown);
    this.target.removeEventListener("pointermove", this.onMove);
    this.target.removeEventListener("pointerup", this.onUp);
    this.target.removeEventListener("pointercancel", this.onUp);
    this.target.removeEventListener("pointerleave", this.onLeave);
  }

  private local(e: PointerEvent): { x: number; y: number } {
    const rect = this.target.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  private sampleOf(
    id: number,
    x: number,
    y: number,
    role: PointerRole,
  ): PointerSample {
    return { id, x, y, dx: 0, dy: 0, role, intensity: 0 };
  }

  private emitBubbleStart(sample: PointerSample): void {
    for (const fn of this.bubbleStartHandlers) fn(sample);
  }

  private emitBubbleMove(sample: PointerSample): void {
    for (const fn of this.bubbleMoveHandlers) fn(sample);
  }

  private emitBubbleEnd(sample: PointerSample): void {
    for (const fn of this.bubbleEndHandlers) fn(sample);
  }

  private emitCurrentEnd(sample: PointerSample): void {
    for (const fn of this.currentEndHandlers) fn(sample);
  }

  private clearDwell(p: TrackedPointer): void {
    if (p.dwellTimer === null) return;
    window.clearTimeout(p.dwellTimer);
    p.dwellTimer = null;
  }

  private commitBubble(id: number): void {
    const p = this.pointers.get(id);
    if (!p || p.role !== "pending") return;
    this.clearDwell(p);
    p.role = "bubble";
    this.emitBubbleStart(this.sampleOf(id, p.x, p.y, "bubble"));
  }

  private commitCurrent(id: number): void {
    const p = this.pointers.get(id);
    if (!p || p.role !== "pending") return;
    this.clearDwell(p);
    p.role = "current";
  }

  private onDown(e: PointerEvent): void {
    e.preventDefault();
    if (e.pointerType === "mouse" && e.button === 2) return;

    try {
      this.target.setPointerCapture(e.pointerId);
    } catch {
      /* synthetic events and some touch paths cannot capture */
    }
    const { x, y } = this.local(e);
    const touch = e.pointerType === "touch";

    if (!touch) {
      this.pointers.set(e.pointerId, {
        x,
        y,
        originX: x,
        originY: y,
        role: "bubble",
        dwellTimer: null,
      });
      this.emitBubbleStart(this.sampleOf(e.pointerId, x, y, "bubble"));
      return;
    }

    const tracked: TrackedPointer = {
      x,
      y,
      originX: x,
      originY: y,
      role: "pending",
      dwellTimer: null,
    };
    this.pointers.set(e.pointerId, tracked);
    const dwell = Math.max(0, this.touchDwellMs());
    tracked.dwellTimer = window.setTimeout(() => {
      this.commitBubble(e.pointerId);
    }, dwell);
  }

  private onMove(e: PointerEvent): void {
    const { x, y } = this.local(e);

    if (e.pointerType === "mouse" || e.pointerType === "pen") {
      const gust = this.sampleMotion(e.pointerId, x, y, e.timeStamp);
      const kin = this.motion.get(e.pointerId);
      const dx = kin?.vx ?? 0;
      const dy = kin?.vy ?? 0;
      const hover: PointerSample = {
        id: e.pointerId,
        x,
        y,
        dx,
        dy,
        role: "current",
        intensity: Math.hypot(dx, dy),
      };
      for (const fn of this.hoverHandlers) fn(hover);
      if (gust) {
        for (const fn of this.currentStrokeHandlers) fn(gust);
      }
    }

    const prev = this.pointers.get(e.pointerId);
    if (!prev) return;
    prev.x = x;
    prev.y = y;

    if (prev.role === "pending") {
      const slop = Math.max(4, this.touchSlopPx());
      const dist = Math.hypot(x - prev.originX, y - prev.originY);
      if (dist > slop) this.commitCurrent(e.pointerId);
    }

    if (prev.role === "bubble") {
      this.emitBubbleMove(this.sampleOf(e.pointerId, x, y, "bubble"));
    } else if (prev.role === "current") {
      const gust = this.sampleMotion(e.pointerId, x, y, e.timeStamp);
      if (gust) {
        for (const fn of this.currentStrokeHandlers) fn(gust);
      }
    }
  }

  private onUp(e: PointerEvent): void {
    const prev = this.pointers.get(e.pointerId);
    this.pointers.delete(e.pointerId);
    if (e.pointerType !== "mouse" && e.pointerType !== "pen") {
      this.motion.delete(e.pointerId);
    }
    try {
      this.target.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
    if (!prev) return;
    this.clearDwell(prev);
    const { x, y } = this.local(e);

    if (prev.role === "pending") {
      const tap = this.sampleOf(e.pointerId, x, y, "bubble");
      this.emitBubbleStart(tap);
      this.emitBubbleEnd(tap);
      return;
    }

    const sample = this.sampleOf(e.pointerId, x, y, prev.role);
    if (prev.role === "bubble") this.emitBubbleEnd(sample);
    else this.emitCurrentEnd(sample);
  }

  private onLeave(e: PointerEvent): void {
    if (e.pointerType === "mouse" || e.pointerType === "pen") {
      this.motion.delete(e.pointerId);
      for (const fn of this.hoverEndHandlers) fn();
    }
  }

  private sampleMotion(
    id: number,
    x: number,
    y: number,
    timeStamp: number,
  ): PointerSample | null {
    const prev = this.motion.get(id);
    if (!prev) {
      this.motion.set(id, { x, y, vx: 0, vy: 0, ax: 0, ay: 0, t: timeStamp });
      return null;
    }

    const dt = (timeStamp - prev.t) / 1000;
    if (dt <= 1 / 480) return null;
    if (dt > 0.12) {
      prev.x = x;
      prev.y = y;
      prev.vx = 0;
      prev.vy = 0;
      prev.ax = 0;
      prev.ay = 0;
      prev.t = timeStamp;
      return null;
    }

    const vx = (x - prev.x) / dt;
    const vy = (y - prev.y) / dt;
    const rawAx = (vx - prev.vx) / dt;
    const rawAy = (vy - prev.vy) / dt;
    const follow = 1 - Math.exp(-dt / 0.045);
    prev.vx += (vx - prev.vx) * follow;
    prev.vy += (vy - prev.vy) * follow;
    prev.ax += (rawAx - prev.ax) * follow;
    prev.ay += (rawAy - prev.ay) * follow;
    prev.x = x;
    prev.y = y;
    prev.t = timeStamp;

    const accel = Math.hypot(prev.ax, prev.ay);
    const speed = Math.hypot(prev.vx, prev.vy);
    const ref = Math.max(1, this.accelRef());
    const deadzone = ref * 0.04;
    if (accel < deadzone || speed < 40) return null;

    const intensity = Math.min(this.accelMax(), accel / ref);
    return {
      id,
      x,
      y,
      dx: prev.vx,
      dy: prev.vy,
      role: "current",
      intensity,
    };
  }
}
